import React, { useState, useEffect } from 'react';
import { getDraftPoolGear } from '../../DatabaseAPI';

const GearPoolModal = ({ isOpen, onClose, teamId }) => {
    const [gear, setGear] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen && teamId) {
            fetchGear();
        }
    }, [isOpen, teamId]);

    const fetchGear = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getDraftPoolGear(teamId);
            if (response.success) {
                setGear(response.all_gear || []);
            } else {
                setError(response.message || 'Failed to load gear');
            }
        } catch (err) {
            setError(err.message || 'Failed to load gear');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const getRarityColor = (rarity) => {
        switch (rarity) {
            case 'LEGENDARY': return '#FFD700'; // Gold
            case 'RARE': return '#9A4FFF'; // Purple
            case 'EPIC': return '#FF6B35'; // Orange
            case 'COMMON': return '#8B8B8B'; // Gray
            default: return '#3498db'; // Blue
        }
    };

    const groupedGear = {
        LEGENDARY: gear.filter(g => g.rarity === 'LEGENDARY'),
        RARE: gear.filter(g => g.rarity === 'RARE'),
        EPIC: gear.filter(g => g.rarity === 'EPIC'),
        COMMON: gear.filter(g => g.rarity === 'COMMON')
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                zIndex: 2000,
                padding: '60px 20px 20px 20px',
                overflowY: 'auto'
            }}
            onClick={onClose}
        >
            <div
                style={{
                    backgroundColor: '#2c3e50',
                    border: '3px solid #3498db',
                    borderRadius: '12px',
                    padding: '30px',
                    maxWidth: '900px',
                    width: '100%',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    position: 'relative'
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Close button */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        background: 'rgba(231, 76, 60, 0.8)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '35px',
                        height: '35px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        cursor: 'pointer',
                        color: 'white',
                        fontWeight: 'bold',
                        transition: 'all 0.2s ease'
                    }}
                    onMouseOver={e => e.target.style.backgroundColor = 'rgba(192, 57, 43, 1)'}
                    onMouseOut={e => e.target.style.backgroundColor = 'rgba(231, 76, 60, 0.8)'}
                >
                    ×
                </button>

                {/* Header */}
                <h2 style={{
                    margin: '0 0 20px 0',
                    color: '#3498db',
                    fontSize: '28px',
                    textAlign: 'center',
                    borderBottom: '2px solid #3498db',
                    paddingBottom: '15px'
                }}>
                    Gear Draft Pool
                </h2>

                {loading && (
                    <div style={{ textAlign: 'center', padding: '40px', fontSize: '18px' }}>
                        Loading gear...
                    </div>
                )}

                {error && (
                    <div style={{
                        textAlign: 'center',
                        padding: '20px',
                        backgroundColor: 'rgba(231, 76, 60, 0.2)',
                        borderRadius: '8px',
                        color: '#e74c3c',
                        marginBottom: '20px'
                    }}>
                        {error}
                    </div>
                )}

                {!loading && !error && gear.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', fontSize: '18px', color: '#95a5a6' }}>
                        No gear found in the draft pool.
                    </div>
                )}

                {!loading && !error && gear.length > 0 && (
                    <div>
                        {/* Legendary Gear */}
                        {groupedGear.LEGENDARY.length > 0 && (
                            <div style={{ marginBottom: '30px' }}>
                                <h3 style={{
                                    color: getRarityColor('LEGENDARY'),
                                    fontSize: '22px',
                                    marginBottom: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}>
                                    <span>✨</span>
                                    Legendary ({groupedGear.LEGENDARY.length})
                                </h3>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                    gap: '15px'
                                }}>
                                    {groupedGear.LEGENDARY.map((item, index) => (
                                        <GearCard key={index} gear={item} rarity="LEGENDARY" />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Rare Gear */}
                        {groupedGear.RARE.length > 0 && (
                            <div style={{ marginBottom: '30px' }}>
                                <h3 style={{
                                    color: getRarityColor('RARE'),
                                    fontSize: '22px',
                                    marginBottom: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}>
                                    <span>💎</span>
                                    Rare ({groupedGear.RARE.length})
                                </h3>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                    gap: '15px'
                                }}>
                                    {groupedGear.RARE.map((item, index) => (
                                        <GearCard key={index} gear={item} rarity="RARE" />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Epic Gear */}
                        {groupedGear.EPIC.length > 0 && (
                            <div style={{ marginBottom: '30px' }}>
                                <h3 style={{
                                    color: getRarityColor('EPIC'),
                                    fontSize: '22px',
                                    marginBottom: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}>
                                    <span>🔥</span>
                                    Epic ({groupedGear.EPIC.length})
                                </h3>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                    gap: '15px'
                                }}>
                                    {groupedGear.EPIC.map((item, index) => (
                                        <GearCard key={index} gear={item} rarity="EPIC" />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Common Gear */}
                        {groupedGear.COMMON.length > 0 && (
                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{
                                    color: getRarityColor('COMMON'),
                                    fontSize: '22px',
                                    marginBottom: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}>
                                    <span>📦</span>
                                    Common ({groupedGear.COMMON.length})
                                </h3>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                    gap: '15px'
                                }}>
                                    {groupedGear.COMMON.map((item, index) => (
                                        <GearCard key={index} gear={item} rarity="COMMON" />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const GearCard = ({ gear, rarity }) => {
    const getRarityColor = (rarity) => {
        switch (rarity) {
            case 'LEGENDARY': return '#FFD700';
            case 'RARE': return '#9A4FFF';
            case 'EPIC': return '#FF6B35';
            case 'COMMON': return '#8B8B8B';
            default: return '#3498db';
        }
    };

    return (
        <div style={{
            backgroundColor: '#34495e',
            border: `2px solid ${getRarityColor(rarity)}`,
            borderRadius: '8px',
            padding: '15px',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            cursor: 'default'
        }}
        onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = `0 6px 20px ${getRarityColor(rarity)}40`;
        }}
        onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
        }}
        >
            <div style={{
                fontWeight: 'bold',
                fontSize: '16px',
                marginBottom: '8px',
                color: 'white'
            }}>
                {gear.name}
            </div>
            
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '13px',
                color: '#bdc3c7',
                marginBottom: '8px'
            }}>
                <span>{gear.slot}</span>
                <span style={{ color: getRarityColor(rarity), fontWeight: 'bold' }}>
                    {rarity}
                </span>
            </div>

            <div style={{
                fontSize: '13px',
                color: '#95a5a6',
                marginTop: '8px',
                paddingTop: '8px',
                borderTop: '1px solid #4a5f7f',
                lineHeight: '1.4'
            }}>
                {gear.description}
            </div>
        </div>
    );
};

export default GearPoolModal;